import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format as formatDateFn } from "date-fns";

import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";

import en from "date-fns/locale/en-GB";
import fr from "date-fns/locale/fr";
import es from "date-fns/locale/es";
import de from "date-fns/locale/de";
import it from "date-fns/locale/it";
import nl from "date-fns/locale/nl";
import pl from "date-fns/locale/pl";
import pt from "date-fns/locale/pt";
import cs from "date-fns/locale/cs";
import { Label } from "@fluentui/react-components";

// Register locales
registerLocale("en", en);
registerLocale("fr", fr);
registerLocale("es", es);
registerLocale("de", de);
registerLocale("it", it);
registerLocale("nl", nl);
registerLocale("pl", pl);
registerLocale("pt", pt);
registerLocale("cs", cs);

const CustomDatePicker = ({
  selected,
  value,
  onChange,
  id = "datepicker",
  dateFormat = "dd/MM/yyyy",
  minDate,
  shouldCloseOnSelect = true,
  wrapperClass = "",
  preventMonthAutoSelect = false,
  ...props
}) => {
  const [internalDate, setInternalDate] = useState(
    selected ?? value ?? null
  );

  const [initialDate, setInitialDate] = useState(null);
  const [isChanged, setIsChanged] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(selected ?? value ?? new Date());

  const { i18n } = useTranslation();
  const locale = i18n.language.split("-")[0];

  // sync state
  useEffect(() => {
    if (selected !== undefined) {
      setInternalDate(selected);
    } else if (value !== undefined) {
      setInternalDate(value);
    }
  }, [selected, value]);

  useEffect(() => {
    if (internalDate) {
      setPickerMonth(internalDate);
    }
  }, [internalDate]);

  // store initial date once
  useEffect(() => {
    if (internalDate && !initialDate) {
      setInitialDate(internalDate);
    }
  }, [internalDate]);

  const handleChange = (date) => {
    if (!date) return;

    if (initialDate) {
      const changed =
        new Date(date).getTime() !== new Date(initialDate).getTime();
      setIsChanged(changed);
    } else {
      setInitialDate(date);
      setIsChanged(true);
    }

    if (onChange) {
      onChange(date);
    } else {
      setInternalDate(date);
    }
  };

  const isSameMonthAndYear = (date1, date2) => {
    if (!date1 || !date2) return false;

    return (
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const shouldHideKeyboardSelection =
    preventMonthAutoSelect && !isSameMonthAndYear(internalDate, pickerMonth);

  const calendarClassName = [
    "custom-date-picker-calendar",
    shouldHideKeyboardSelection ? "datepicker-hide-keyboard-selection" : "",
    props.calendarClassName || "",
  ]
    .filter(Boolean)
    .join(" ");

  const inputValue = internalDate
    ? formatDateFn(internalDate, dateFormat)
    : "";

  const handleCalendarOpen = () => {
    setPickerMonth(internalDate || new Date());
    props.onCalendarOpen?.();
  };

  const handleMonthChange = (date) => {
    setPickerMonth(date);
    props.onMonthChange?.(date);
  };

  return (
    <div
      className={`date-input position-relative ${
        isChanged ? "ctm-success" : ""
      } ${wrapperClass}`}
    >
    
      <DatePicker
        locale={locale}
        id={id}
        className="form-control bg-white"
        selected={
          preventMonthAutoSelect && !isSameMonthAndYear(internalDate, pickerMonth)
            ? null
            : internalDate
        }
        value={preventMonthAutoSelect ? inputValue : undefined}
        openToDate={preventMonthAutoSelect ? pickerMonth : undefined}
        calendarClassName={calendarClassName}
        onChange={handleChange}
        onCalendarOpen={handleCalendarOpen}
        onMonthChange={handleMonthChange}
        dateFormat={dateFormat}
        minDate={minDate}
        shouldCloseOnSelect={shouldCloseOnSelect}
        showPopperArrow={false}
        calendarStartDay={1}
        {...props}
      />
    
    </div>
  );
};

export default CustomDatePicker;
